import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { Crown, Gem, LogOut, CreditCard, Menu, Settings, ChevronDown } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { TimezoneSelector } from "@/components/TimezoneSelector";
import { getBillingStatusLabel } from "@/lib/billingAccess";
import { useJotformPlanMatch } from "@/hooks/useJotformPlanMatch";
import { determinePlanFlow, getTierIdFromSubscription, type TierId } from "@/lib/planFlow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const Navbar = () => {
  const { user, signOut, subscription, isSubscriptionLoading } = useAuth();
  const navigate = useNavigate();
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const canManageBilling = Boolean(
    subscription?.subscribed
      || subscription?.billing_has_history
      || subscription?.stripe_customer_id,
  );
  const jotformPlanMatch = useJotformPlanMatch();
  const planDecision = determinePlanFlow({
    currentTierId: getTierIdFromSubscription(subscription),
    matchedTierId: (jotformPlanMatch.data?.matchedTier.id as TierId | undefined) || null,
    currentPriceIdKey: subscription?.active_price_id_key || null,
    subscribed: subscription?.subscribed,
    billingHasHistory: subscription?.billing_has_history,
    billingAccessActive: subscription?.billing_access_active,
  });
  const planAction = !isSubscriptionLoading && jotformPlanMatch.data && (
    planDecision.mode === "upgrade"
      || planDecision.mode === "reinstate"
      || planDecision.mode === "contact"
  )
    ? {
        mode: planDecision.mode,
        href: planDecision.mode === "contact"
          ? "mailto:support@fab.day?subject=Enterprise%20Plan%20Inquiry"
          : `/select-tier?action=${planDecision.mode}`,
        label: planDecision.mode === "reinstate"
          ? "Reinstate"
          : planDecision.mode === "upgrade"
            ? "Upgrade"
            : "Contact Us",
      }
    : null;

  // Environment indicator — detect local dev by hostname
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  );
  const environment = isLocalhost ? 'local' : import.meta.env.VITE_ENVIRONMENT;
  const showEnvironment = environment && environment !== 'production';

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    setIsPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("Could not retrieve customer portal URL.");
      }
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to open customer portal.",
      });
    } finally {
      setIsPortalLoading(false);
    }
  };
  
  const getPlanIcon = (tier: string | null | undefined) => {
    if (!tier) return null;
    if (tier.toLowerCase().includes("pro")) return <Gem className="w-4 h-4 text-primary mr-1" />; 
    if (tier.toLowerCase().includes("enterprise")) return <Crown className="w-4 h-4 text-amber-500 mr-1" />;
    return null;
  };

  const getPlanDisplayName = () => {
    if (isSubscriptionLoading) return "Loading plan...";
    if (!subscription) return "Free Plan";

    const tierName = subscription.subscription_tier || "Free";
    const cycle = subscription.active_price_id_key?.includes('annual') ? "Annual" : "Monthly";

    if (subscription.subscribed) {
      return `${tierName} (${cycle})`;
    }

    if (subscription.billing_has_history) {
      return `${tierName} - ${getBillingStatusLabel(subscription.billing_status)}`;
    }

    return `${tierName} Plan`;
  }

  return (
    <nav className="bg-background border-b border-border px-4 py-2.5 sticky top-0 z-50 overflow-x-hidden">
      <div className="flex justify-between items-center mx-auto max-w-screen-xl w-full">
        <Link to="/" className="flex items-center flex-shrink-0">
          <Logo />
        </Link>

        {/* Environment indicator - center of navbar */}
        {showEnvironment && (
          <div className="hidden sm:flex items-center">
            <div className={`px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wider ${
              environment === 'staging'
                ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
                : environment === 'dev'
                ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                : environment === 'local'
                ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                : 'bg-gray-500/20 text-gray-600 dark:text-gray-400'
            }`}>
              {environment}
            </div>
          </div>
        )}

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2 flex-shrink">
          {/* Common links - show features/pricing/about only for logged out users */}
          {!user && (
            <>
              <Link to="/features">
                <Button variant="ghost" className="text-foreground hover:text-primary">Features</Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-foreground hover:text-primary">
                    Solutions
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  <DropdownMenuItem onClick={() => navigate("/agencies")}>
                    For Agencies
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/it-compliance")}>
                    For IT &amp; Compliance
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/jotform-backup">
                <Button variant="ghost" className="text-foreground hover:text-primary">Jotform Backup</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost" className="text-foreground hover:text-primary">Pricing</Button>
              </Link>
              <Link to="/about">
                <Button variant="ghost" className="text-foreground hover:text-primary">About Us</Button>
              </Link>
            </>
          )}

          {/* Help link for everyone */}
          <Link to="/help">
            <Button variant="ghost" className="text-foreground hover:text-primary">Help</Button>
          </Link>

          {/* Timezone selector for everyone */}
          <TimezoneSelector />

          {/* Theme toggle for everyone */}
          <ThemeToggle />

          {user && planAction && (
            planAction.mode === "contact" ? (
              <Button asChild variant="outline">
                <a href={planAction.href}>{planAction.label}</a>
              </Button>
            ) : (
              <Link to={planAction.href}>
                <Button variant={planAction.mode === "reinstate" ? "default" : "outline"}>
                  {planAction.label}
                </Button>
              </Link>
            )
          )}

          {user ? (
            <div className="flex items-center space-x-4">
              {/* User dropdown menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user.email ? user.email.substring(0, 2).toUpperCase() : "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none flex items-center">
                        {getPlanIcon(subscription?.subscription_tier)}
                        {getPlanDisplayName()}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {/* Account Settings */}
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    <Settings className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>

                  {canManageBilling && (
                    <>
                      <DropdownMenuItem
                        onClick={handleManageSubscription}
                        disabled={isPortalLoading || isSubscriptionLoading}
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        {isPortalLoading ? "Loading..." : "Manage Billing"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button onClick={() => navigate("/auth")}>Sign In</Button>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="flex md:hidden items-center gap-1.5">
          <ThemeToggle />

          {/* Show Sign In button on mobile for non-authenticated users */}
          {!user && (
            <Button onClick={() => navigate("/auth")} size="sm" className="text-xs px-2">Sign In</Button>
          )}

          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden h-9 w-9">
                <Menu className="h-4 w-4" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col space-y-4 mt-6">
                {/* Common links - show features/pricing/about only for logged out users */}
                {!user && (
                  <>
                    <Link to="/features" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        Features
                      </Button>
                    </Link>
                    <p className="px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Solutions
                    </p>
                    <Link to="/agencies" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        For Agencies
                      </Button>
                    </Link>
                    <Link to="/it-compliance" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        For IT &amp; Compliance
                      </Button>
                    </Link>
                    <Link to="/jotform-backup" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        Jotform Backup
                      </Button>
                    </Link>
                    <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        Pricing
                      </Button>
                    </Link>
                    <Link to="/about" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                        About Us
                      </Button>
                    </Link>
                  </>
                )}

                {/* Help link for everyone */}
                <Link to="/help" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary">
                    Help
                  </Button>
                </Link>

                {/* Timezone selector */}
                <div className="px-2">
                  <TimezoneSelector />
                </div>

                {user && planAction && (
                  planAction.mode === "contact" ? (
                    <Button asChild variant="outline" className="w-full justify-start">
                      <a href={planAction.href}>{planAction.label}</a>
                    </Button>
                  ) : (
                    <Link to={planAction.href} onClick={() => setIsMobileMenuOpen(false)}>
                      <Button
                        variant={planAction.mode === "reinstate" ? "default" : "outline"}
                        className="w-full justify-start"
                      >
                        {planAction.label}
                      </Button>
                    </Link>
                  )
                )}

                {user ? (
                  <>
                    <div className="border-t pt-4">
                    <div className="flex flex-col space-y-2 px-2 mb-4">
                      <p className="text-sm font-medium flex items-center">
                        {getPlanIcon(subscription?.subscription_tier)}
                        {getPlanDisplayName()}
                      </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </p>
                      </div>

                      {/* Account Settings */}
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate("/account");
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Account Settings
                      </Button>

                      {canManageBilling && (
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            handleManageSubscription();
                            setIsMobileMenuOpen(false);
                          }}
                          disabled={isPortalLoading || isSubscriptionLoading}
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          {isPortalLoading ? "Loading..." : "Manage Billing"}
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          handleSignOut();
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign Out
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
